<template>
  <Transition name="modal">
    <div v-if="show" class="modal-mask" ref="modal" v-hotkey="keymap">
      <div class="modal-container" :class="theme" :style="{ width, height }">
        <!-- Modal Header Area Start -->
        <div class="modal-header" v-if="header">
          <slot name="header">
            <div class="d-flex justify-center align-center">
              <h3 v-if="title" class="modal-title">{{ title }}</h3>
            </div>
            <img
              src="/img/icon40_closed_40px.svg"
              class="close-btn"
              alt="close"
              @click="close"
            />
          </slot>
        </div>
        <!-- Modal Header Area End -->

        <!-- Modal Body Area Start -->
        <div
          v-if="!header && content && icon"
          class="d-flex justify-end mt-5 mx-5"
        >
          <img
            src="/img/close-gray.svg"
            class="close-btn"
            style="cursor: pointer"
            alt="close"
            @click="close"
          />
        </div>
        <div class="modal-body pt-25 pb-65">
          <div
            v-if="icon && iconSource"
            class="simple-modal-body mt-6"
            style="margin-bottom: 25px"
          >
            <slot name="simple">
              <div
                :class="`${
                  icon === 'treeD' ? '' : 'simple-modal-icon'
                } d-flex justify-center align-center`"
              >
                <img :src="iconSource" alt="icon" />
              </div>
            </slot>
          </div>
          <slot name="body">
            <div class="simple-modal-body mt-6" v-if="content">
              <span>{{ content }}</span>
            </div>
          </slot>
          <span v-if="caption" class="caption">{{ caption }}</span>
        </div>
        <!-- Modal Body Area End -->

        <!-- Modal Footer Area Start -->
        <div class="modal-footer">
          <slot name="footer">
            <div class="actions" v-if="positiveOpts.name || negativeOpts.name">
              <div class="d-flex">
                <c-btn
                  v-if="positiveOpts.name"
                  :name="positiveOpts.name"
                  :img="positiveOpts.img"
                  :disabled="positiveOpts.disabled"
                  :disableImg="positiveOpts.disableImg"
                  :prependIcon="positiveOpts.prependIcon"
                  :width="positiveOpts.width"
                  :height="positiveOpts.height"
                  :fontColor="positiveOpts.fontColor"
                  :title="positiveOpts.title"
                  @click="submit"
                />
                <c-btn
                  style="margin-left: 10px"
                  v-if="negativeOpts.name"
                  :name="negativeOpts.name"
                  :img="negativeOpts.img"
                  :width="negativeOpts.width"
                  :height="negativeOpts.height"
                  :fontColor="negativeOpts.fontColor"
                  :title="negativeOpts.title"
                  @click="close"
                />
              </div>
            </div>
          </slot>
        </div>
        <!-- Modal Footer Area End -->
      </div>
    </div>
  </Transition>
</template>

<script>
import util from "@/common/util";
import CBtn from "@/components/CBtn.vue";

export default {
  components: {
    "c-btn": CBtn,
  },
  props: {
    /** 모달 열림/닫힘 상태 (sync) */
    open: {
      type: Boolean,
      default: () => false,
    },
    /**
     * 모달 테마
     * normal
     * warn
     * */
    theme: {
      type: String,
      default: () => "normal",
    },
    /** 모달 너비 */
    width: {
      type: String,
      default: () => "100%",
    },
    /** 모달 높이 */
    height: {
      type: String,
      default: () => "",
    },
    header: {
      type: Boolean,
      default: () => true,
    },
    title: {
      type: String,
      default: () => "",
    },
    /** 모달 캡션 (simple modal) */
    caption: {
      type: String,
      default: () => "",
    },
    /** 모달 내용 (simple modal) */
    content: {
      type: String,
      default: () => "",
    },
    /** 모달 아이콘 (simple modal)
     * confirm
     * send
     * warn
     */
    icon: {
      type: String,
      default: () => "",
    },
    /**
     * 모달 close 시 바로 닫힘 여부
     * false: 모달창 바로 닫힘
     * true: 모달창 바로 닫히지 않음 (컴포넌트 외부에서 close)
     */
    closeExternal: {
      type: Boolean,
      default: () => false,
    },
    /**
     * Enter Key Event 로 submit 여부
     * true: enter 버튼 눌렀을 때 submit
     * false: enter 버튼 비활성화 (기본)
     */
    enterPositive: {
      type: Boolean,
      default: () => false,
    },
    /**
     * Esc Key Event 로 모달 close 여부
     * true: esc 버튼 눌렀을 때 모달 close (기본)
     * false: esc 버튼 비활성화
     */
    escapePositive: {
      type: Boolean,
      default: () => true,
    },
    btnPositive: {
      type: Object,
      default: () => {
        return {
          name: "",
          img: "/img/btn/btn_blue_134x50_fill.svg",
          disabled: false,
          disableImg: "/img/btn/btn_gray_134x50_fill.svg",
          prependIcon: "",
          width: "134px",
          height: "50px",
          fontColor: "#ffffff",
          title: "",
        };
      },
    },
    btnNegative: {
      type: Object,
      default: () => {
        return {
          name: "",
          img: "/img/btn/btn_gray_134x50.svg",
          disabled: false,
          disableImg: "/img/btn/btn_gray_134x50_fill.svg",
          prependIcon: "",
          width: "134px",
          height: "50px",
          fontColor: "#999999",
          title: "",
        };
      },
    },
  },
  watch: {
    open() {
      this.show = this.open;
    },
    show() {
      if (this.show) {
        this.createKey();
        this.$nextTick(() => {
          this.$refs.modal.focus();
        });
        return;
      }
      this.destroyKey();
    },
    btnPositive: {
      deep: true,
      handler() {
        this.positiveOpts = {
          ...this.positiveOpts,
          ...this.btnPositive,
        };
      },
    },
    btnNegative: {
      deep: true,
      handler() {
        this.negativeOpts = {
          ...this.negativeOpts,
          ...this.btnNegative,
        };
      },
    },
    icon() {
      this.iconSource = this.iconSourceList[this.icon];
    },
  },
  computed: {
    modalStack() {
      return this.$store.getters.common.modalStack;
    },
    keymap() {
      return {
        escape: {
          keydown: () => {
            if (!this.escapePositive) {
              return;
            }

            this.closeWithKey();
          },
        },
        enter: {
          keydown: () => {
            this.submitWithKey();
          },
        },
      };
    },
  },
  mounted() {
    this.show = this.open;

    this.positiveOpts = { ...this.positiveOpts, ...this.btnPositive };
    this.negativeOpts = { ...this.negativeOpts, ...this.btnNegative };
  },
  data: () => {
    return {
      show: false,
      id: "",
      positiveOpts: {
        name: "",
        img: "/img/btn/btn_blue_134x50_fill.svg",
        disabled: false,
        disableImg: "/img/btn/btn_gray_134x50_fill.svg",
        prependIcon: "",
        width: "134px",
        height: "50px",
        fontColor: "#ffffff",
      },
      negativeOpts: {
        name: "",
        img: "/img/btn/btn_gray_134x50.svg",
        disabled: false,
        disableImg: "/img/btn/btn_gray_134x50_fill.svg",
        prependIcon: "",
        width: "134px",
        height: "50px",
        fontColor: "#999999",
      },
      iconSource: "",
      iconSourceList: {
        //confirm: "/img/simple-modal-confirm-blue.svg",
        //send: "/img/simple-modal-send-blue.svg",
        warn: "/img/simple-modal-warn-blue.svg",
        treeD: "/img/simple-modal-treed.svg",
      },
    };
  },
  methods: {
    createKey() {
      this.id = util.getRandomKey(this.modalStack);
      this.$store.commit("common/addModalStack", this.id);
    },
    destroyKey() {
      this.$store.commit("common/removeModalStack", this.id);
    },
    closeWithKey() {
      const closeable = this.isModalCloseable(this.id);
      if (closeable) {
        this.close();
      }
    },
    close() {
      if (!this.closeExternal) {
        this.$emit("update:open", false);
      }

      this.$emit("negative");
      this.$emit("close");
    },
    isModalCloseable(id) {
      const lastId = this.modalStack.at(-1);
      if (lastId === id) {
        return true;
      }
      return false;
    },
    submit() {
      this.$emit("submit");
    },
    submitWithKey() {
      if (this.enterPositive) {
        this.submit();
      }
    },
  },
};
</script>

<style scoped>
.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(51, 51, 51, 0.4);
  backdrop-filter: blur(2.5px);
  display: flex;
  transition: opacity 0.3s ease;
  padding: 100px;
}

.modal-container {
  margin: auto;
  background-color: #fff;
  box-shadow: -4px 4px 20px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  padding: 10px 20px;
  height: 60px;
}

.modal-header h3 {
  font-size: 18px;
  font-weight: 600;
  margin-top: 0;
}

.modal-header .close-btn {
  cursor: pointer;
}

.modal-body {
  padding: 20px;
  /* overflow: auto; */
  /* max-height: 650px; */
}

.modal-body .simple-modal-body {
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-body .simple-modal-body span {
  white-space: pre-line;
  font-size: 18px;
  font-weight: 500;
  color: #666666;
  text-align: center;
}

.modal-body .simple-modal-body strong {
  font-size: 18px;
  font-weight: 600;
  color: #333333;
  text-align: center;
}

.modal-body .simple-modal-body .simple-modal-icon {
  width: 80px;
  height: 80px;
  background-color: #f7f7f7;
  border-radius: 50%;
}

.modal-body .caption {
  display: flex;
  justify-content: center;
  align-items: center;
  color: #c83c50;
  font-family: Pretendard !important;
  font-size: 12px !important;
}

.modal-footer {
  display: flex;
  justify-content: center;
}

.modal-footer .actions {
  padding-bottom: 60px;
}

.modal-enter {
  opacity: 0;
}

.modal-leave-to {
  opacity: 0;
}

.modal-enter .modal-container,
.modal-leave-to .modal-container {
  -webkit-transform: scale(1.1) !important;
  transform: scale(1.1) !important;
}

.modal-container.normal .modal-header {
  background-color: #0085cf;
}

.modal-container.warn .modal-header {
  background-color: #c83c50;
}

.modal-container.normal .modal-header h3 {
  color: #ffffff;
}
.pb-65 {
  padding-bottom: 65px !important;
}
.pt-25 {
  padding-top: 25px !important;
}
</style>
